import type { ICampaignHostPackage } from '../types/global/campaignHostSubscription.types';
import type {
    ICampaignHostLtdPromotionAdminDraft,
    ICampaignHostLtdPromotionAdminResponse,
    ILtdPromotionAdminPatch,
} from '../types/global/campaignHostAdmin.types';

export interface CampaignHostStripePriceOption {
    value: string;
    label: string;
}

export const DEFAULT_PER_CAMPAIGN_FEE_CENTS = 5000;

export function adminResponseToDraft(
    data: ICampaignHostLtdPromotionAdminResponse,
): ICampaignHostLtdPromotionAdminDraft {
    const pricing = data.campaign_host_pricing;
    const promo = pricing.ltd_promotion;
    return {
        enabled: Boolean(promo.enabled),
        starts_at: promo.starts_at,
        ends_at: promo.ends_at,
        stripe_price_id: promo.stripe_price_id ?? '',
        promo_price_cents: promo.promo_price_cents,
        per_campaign_fee_cents: pricing.per_campaign_fee_cents ?? DEFAULT_PER_CAMPAIGN_FEE_CENTS,
    };
}

export function buildLtdPromotionAdminPatch(
    snapshot: ICampaignHostLtdPromotionAdminDraft,
    draft: ICampaignHostLtdPromotionAdminDraft,
): ILtdPromotionAdminPatch {
    const patch: ILtdPromotionAdminPatch = {};
    const normStripe = (v: string) => v.trim() || null;

    if (draft.enabled !== snapshot.enabled) patch.enabled = draft.enabled;
    if (draft.starts_at !== snapshot.starts_at) patch.starts_at = draft.starts_at;
    if (draft.ends_at !== snapshot.ends_at) patch.ends_at = draft.ends_at;
    if (normStripe(draft.stripe_price_id) !== normStripe(snapshot.stripe_price_id)) {
        patch.stripe_price_id = normStripe(draft.stripe_price_id);
    }
    if (draft.promo_price_cents !== snapshot.promo_price_cents) {
        patch.promo_price_cents = draft.promo_price_cents;
    }
    if (draft.per_campaign_fee_cents !== snapshot.per_campaign_fee_cents) {
        patch.per_campaign_fee_cents = draft.per_campaign_fee_cents;
    }

    return patch;
}

/** Stripe price IDs from GET /campaign-host-packages (for admin promo dropdown). */
export function buildCampaignHostStripePriceOptions(
    packages: ICampaignHostPackage[] | unknown,
    currentPriceId?: string,
): CampaignHostStripePriceOption[] {
    const list = Array.isArray(packages) ? packages : [];
    const seen = new Set<string>();
    const options: CampaignHostStripePriceOption[] = [];

    const add = (priceId: string, label: string) => {
        const id = priceId.trim();
        if (!id || seen.has(id)) return;
        seen.add(id);
        options.push({ value: id, label });
    };

    for (const pkg of list) {
        const priceId = pkg.effective_stripe_price_id ?? pkg.stripe_price_id;
        if (!priceId) continue;
        const display = pkg.formatted_for_display ? ` (${pkg.formatted_for_display})` : '';
        add(priceId, `${pkg.name}${display} — ${priceId}`);
    }

    const saved = currentPriceId?.trim();
    if (saved && !seen.has(saved)) {
        add(saved, `${saved} (saved)`);
    }

    return options.sort((a, b) => a.label.localeCompare(b.label));
}

export function centsToDollarInput(cents: number | null): string {
    if (cents == null) return '';
    return (cents / 100).toFixed(2);
}

export function dollarInputToCents(value: string): number | null {
    const n = parseFloat(value.replace(/[$,]/g, '').trim());
    if (!Number.isFinite(n)) return null;
    return Math.round(n * 100);
}
